import { formatBuyerCriteriaLine, getBuyerNarrativeSummary } from './buyerProfileDisplay';

export function resumeRecapStorageKey(slug) {
  return `a4a_resume_recap_${slug}`;
}

/** Portal / login: show recap when opening a fresh chat (not full transcript replay). */
export function markResumeRecap(slug) {
  if (!slug) return;
  sessionStorage.setItem(resumeRecapStorageKey(slug), '1');
}

export function peekResumeRecap(slug) {
  if (!slug) return false;
  return sessionStorage.getItem(resumeRecapStorageKey(slug)) === '1';
}

export function clearResumeRecap(slug) {
  if (!slug) return;
  sessionStorage.removeItem(resumeRecapStorageKey(slug));
}

/** @deprecated Prefer peek at start + clear after messages are shown */
export function consumeResumeRecap(slug) {
  const should = peekResumeRecap(slug);
  if (should) clearResumeRecap(slug);
  return should;
}

/** Showings still in play — completed/cancelled drop off the recap. */
const ACTIVE_SHOWING_STATUSES = new Set([
  'pending',
  'scheduled',
  'confirmation_sent',
  'confirmed',
]);

function formatShowingAddress(showing) {
  const p = showing?.property;
  if (!p) return null;
  const parts = [p.address, p.city].filter(Boolean);
  if (parts.length) return parts.join(', ');
  if (p.mls_number) return `MLS ${p.mls_number}`;
  return null;
}

function isActiveShowing(showing) {
  return ACTIVE_SHOWING_STATUSES.has(String(showing?.status || 'pending').toLowerCase());
}

function formatSavedSearchRecapLine(buyerProfile) {
  const criteriaLine = formatBuyerCriteriaLine(buyerProfile);
  if (!criteriaLine) return null;
  return `Your saved search: ${criteriaLine}. Tell me if you'd like to change your budget, areas, or must-haves.`;
}

/**
 * Start intent for a fresh chat thread when we already know buyer vs seller.
 */
export function resolveReturningClientStartIntent(client) {
  if (!client) return null;
  const role = client.client_role || 'buyer';
  const buyerProfile = client.buyer_profile;
  const sellerProfile = client.seller_profile;
  const hasBuyerSearch = Boolean(
    formatBuyerCriteriaLine(buyerProfile) || getBuyerNarrativeSummary(buyerProfile)
  );
  const hasSellerProfile = Boolean(
    sellerProfile?.property_address
    || sellerProfile?.property_city
    || sellerProfile?.timeline
  );

  if (role === 'seller') return 'seller_guided';
  if (role === 'buyer') return 'buyer_guided';
  if (hasBuyerSearch && !hasSellerProfile) return 'buyer_guided';
  if (hasSellerProfile && !hasBuyerSearch) return 'seller_guided';
  if (hasBuyerSearch) return 'buyer_guided';
  if (hasSellerProfile) return 'seller_guided';
  return 'buyer_guided';
}

function lastUserSnippet(messages, maxLen = 100) {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'user') {
      const t = (messages[i].content || '').trim();
      if (!t) continue;
      return t.length > maxLen ? `${t.slice(0, maxLen)}…` : t;
    }
  }
  return null;
}

function formatSellerPropertyLine(profile) {
  if (!profile) return null;
  const parts = [profile.property_address, profile.property_city].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

function buildBuyerRecapBody({
  messages = [],
  buyerProfile,
  showings = [],
  agentFirstName = 'your agent',
}) {
  const bits = [];
  const activeShowings = showings.filter(isActiveShowing);
  const latestShowing = activeShowings[0];
  const showingAddr = latestShowing ? formatShowingAddress(latestShowing) : null;

  if (showingAddr) {
    const status = String(latestShowing.status || 'pending').toLowerCase();
    const statusNote = status === 'confirmed' || status === 'scheduled'
      ? `${agentFirstName} has a time lined up — say if you need to reschedule.`
      : `${agentFirstName} has your request and will reach out to confirm a time.`;
    bits.push(`You have an open showing request for ${showingAddr} — ${statusNote}`);
  }

  const savedSearchLine = formatSavedSearchRecapLine(buyerProfile);
  if (savedSearchLine) {
    bits.push(savedSearchLine);
  } else {
    const narrative = getBuyerNarrativeSummary(buyerProfile);
    if (narrative) {
      bits.push(narrative.endsWith('.') ? narrative : `${narrative}.`);
    }
  }

  if (bits.length === 0) {
    const snippet = lastUserSnippet(messages);
    if (snippet) {
      bits.push(`Last time you asked about “${snippet}”.`);
    }
  }

  return bits.join('\n\n');
}

function buildSellerRecapBody({ sellerProfile, agentFirstName = 'your agent' }) {
  const bits = [];
  const place = formatSellerPropertyLine(sellerProfile);
  if (place) bits.push(`Your property on file: ${place}.`);
  if (sellerProfile?.timeline) bits.push(`Selling timeline: ${sellerProfile.timeline}.`);
  if (!bits.length) {
    bits.push(`Your seller consult with ${agentFirstName} is on file — ask about market conditions or next steps anytime.`);
  }
  return bits.join('\n\n');
}

/**
 * Recap for logged-in clients (buyer, seller, or both) — no LLM, no full transcript.
 */
export function buildReturningClientRecap({
  clientRole = 'buyer',
  firstName,
  messages = [],
  buyerProfile,
  sellerProfile,
  showings = [],
  agentFirstName = 'your agent',
}) {
  const sections = [];

  if (clientRole === 'buyer' || clientRole === 'both') {
    const buyerBody = buildBuyerRecapBody({
      messages,
      buyerProfile,
      showings,
      agentFirstName,
    });
    if (buyerBody) {
      sections.push(clientRole === 'both' ? `Buying: ${buyerBody}` : buyerBody);
    }
  }

  if (clientRole === 'seller' || clientRole === 'both') {
    const sellerBody = buildSellerRecapBody({ sellerProfile, agentFirstName });
    if (sellerBody) {
      sections.push(clientRole === 'both' ? `Selling: ${sellerBody}` : sellerBody);
    }
  }

  const name = firstName ? firstName.trim() : 'there';
  if (!sections.length) {
    return `Welcome back, ${name}! Ask about listings, your home, or book a showing — ${agentFirstName} can follow up anytime.`;
  }

  const body = sections.join('\n\n');
  return `Welcome back, ${name}! Here's a quick recap:\n\n${body}`;
}

/** @deprecated Use buildReturningClientRecap */
export function buildChatResumeRecap(opts) {
  return buildReturningClientRecap({ ...opts, clientRole: 'buyer' });
}
